// @ts-strict-ignore
import * as React from 'react';
import { generateUrl } from '../../api';
import { releaseStepDetailsContainerModuleStyle, antGrey, titleStyle, runwayColors } from '../styles/globalStyles';
import {
    AppDistributionService,
    AppStoreUploadArtifactStatus,
    BuildArtifact,
    BuildDistroArtifactInfo,
    BuildStatus,
    DsymUploadArtifactStatus,
    IntegrationCapability,
    IntegrationInstallationId,
    RunwayResponseType,
    StabilityMonitoringService,
} from '../../types';
import Text from 'antd/lib/typography/Text';
import List from 'antd/lib/list';
import {
    FileTextOutlined,
    InfoCircleOutlined,
    QrcodeOutlined,
    ReloadOutlined,
    MobileOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectAuthCredentials,
    selectEffectiveTimezone,
    selectIntegrationForCapability,
    selectJoinedCommsProvidersNamesList,
    selectStepRun,
    selectUserCanPerformAction,
} from '../../selectors/selectors';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import * as API from '../../api';
import { RootState } from '../../reducers/reducers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import AutomatedByRunwayIcon from '../AutomatedByRunwayIcon';
import { globalMessage } from '../../utils/globalMessage';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { QRCode } from 'react-qrcode-logo';
import Modal from 'antd/lib/modal';

import { RetryAppStoreArtifactUploadRequest, RetryDsymUploadRequest, tryUpdateRelease } from '../../actions/release';
import { tagStyle } from '../styles/globalStyles';
import { ReleaseUpdatable } from '../../actions/updatable';
import {
    isStabilityMonitoringService,
    isAppDistributionService,
    getShouldDisableInstallationForBinaryType,
    bytesToMB,
} from '../../helpers';
import { AppComponentProps, Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import AuthedUrlButton from '../authedUrlButton/AuthedUrlButton';
import { MenuProps } from 'antd/lib/menu';
import Button from 'antd/lib/button';
import { displayTextForAppStoreUploadRule, helperTextForArtifactType } from '../../helpers/displayHelpers';
import { DatetimeFormat } from '../../timeHelpers';
import { dayjs } from '@/extendedDayjs';
import Dropdown from 'antd/lib/dropdown';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { MenuItemType } from 'antd/lib/menu/interface';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { stepRunEndpoint } from '@/helpers/apiHelpers';
import { BinaryBuildDetailsView } from './BinaryBuildDetailsView';
import { UploadStatusSection, statusTagText, statusTagColor } from './UploadStatusSection';

const MAX_ERROR_MESSAGE_LENGTH = 200;

interface ArtifactsModuleProps extends ReleaseComponentProps {
    version: string;
    artifacts: BuildArtifact[];
    lightStyle?: boolean;
    services: Map<IntegrationInstallationId, AppDistributionService>;
    stabilityMonitoring?: StabilityMonitoringService;
    appIdentifiers: Map<IntegrationCapability, string>;
    buildStatus: BuildStatus;
    canRetry: boolean;
    showHeader: boolean;
    isPublic: boolean;
    publicBuildDownloadUrl?: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const ArtifactsModule: React.FC<ArtifactsModuleProps> = (props: ArtifactsModuleProps) => {
    const [showAllArtifacts, setShowAllArtifacts] = React.useState(false);

    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const lightStyles: React.CSSProperties =
        props.lightStyle ?
            {
                border: 'none',
                backgroundColor: runwayColors.white,
                marginTop: 0,
                padding: 0,
            }
        :   {};

    const { sortedTopArtifacts, allSortedArtifacts, artifactsAreOnlyDsyms } = React.useMemo(() => {
        const sortedArtifacts = [...(props.artifacts ?? [])].sort((a1, a2) => a1.fileName.localeCompare(a2.fileName));

        let onlyDsyms = true;
        const dsymArtifacts: BuildArtifact[] = [];
        const appStoreArtifacts: BuildArtifact[] = [];
        const otherArtifacts: BuildArtifact[] = [];

        for (const artifact of sortedArtifacts) {
            if (artifact.dsymUploadInfo) {
                dsymArtifacts.push(artifact);
            } else if (artifact.appStoreInfos || artifact.binaryBuildDetails) {
                appStoreArtifacts.push(artifact);
            } else {
                otherArtifacts.push(artifact);
            }
            if (!artifact.dsymUploadInfo) {
                onlyDsyms = false;
            }
        }

        let topArtifacts: BuildArtifact[] = [];
        let allArtifacts: BuildArtifact[] = [];
        topArtifacts = appStoreArtifacts.concat(dsymArtifacts);
        allArtifacts = topArtifacts.concat(otherArtifacts);

        if (topArtifacts.length === 0) {
            // no artifacts with build info or dsym info, just show first 5 artifacts
            topArtifacts = allArtifacts.slice(0, 5);
        }

        return {
            sortedTopArtifacts: topArtifacts,
            allSortedArtifacts: allArtifacts,
            artifactsAreOnlyDsyms: onlyDsyms,
        };
    }, [props.artifacts]);

    const titleText =
        props.artifacts.length > 0 ?
            artifactsAreOnlyDsyms ? 'dSYMs'
            :   'Artifacts'
        :   'Artifacts';

    const emptyStateText = props.buildStatus == BuildStatus.InProgress ? 'Build in progress...' : 'No artifacts found.';

    const stepRunId = props.stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const shouldUseWorkflowEndpoint = !!stepRun;

    const getWorkflowArtifactDownloadUrl = React.useCallback(
        ({ artifactId, workflowRunId }: BuildArtifact) => {
            const path = `${stepRunEndpoint(props.stepRunComponentProps)}/artifact`;
            const queryParams = { artifactId, buildId: workflowRunId };
            return generateUrl(path, queryParams);
        },
        [props.stepRunComponentProps],
    );

    const artifactsDataSource = showAllArtifacts ? allSortedArtifacts : sortedTopArtifacts;

    return (
        <div
            style={{
                ...releaseStepDetailsContainerModuleStyle,
                backgroundColor: antGrey.grey2,
                ...lightStyles,
            }}
        >
            {props.showHeader && (
                <Space style={{ paddingBottom: 8 }}>
                    <AutomatedByRunwayIcon />
                    {!props.lightStyle && <Text style={titleTextStyle}>{titleText}</Text>}
                </Space>
            )}
            {artifactsDataSource.length > 0 ?
                <List
                    split={false}
                    style={{ marginTop: 12, paddingRight: 12 }}
                    itemLayout="vertical"
                    dataSource={artifactsDataSource}
                    loadMore={
                        !showAllArtifacts && sortedTopArtifacts.length !== allSortedArtifacts.length ?
                            <Button type="link" onClick={() => setShowAllArtifacts(true)}>
                                View all artifacts
                            </Button>
                        :   null
                    }
                    renderItem={(artifact: BuildArtifact, index: number) => {
                        const sizeInMB = bytesToMB(artifact.size);

                        return (
                            <List.Item
                                style={{
                                    border: 'none',
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                    paddingTop: artifact.binaryBuildDetails ? 12 : 4,
                                    paddingBottom: artifact.binaryBuildDetails ? 12 : 4,
                                }}
                                key={index}
                            >
                                <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                                    <Space>
                                        <FileTextOutlined style={{ color: antGrey.grey7, marginTop: 10 }} />
                                    </Space>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Space size={4}>
                                                {!props.isPublic && (
                                                    <AuthedUrlButton
                                                        releaseId={props.releaseId}
                                                        actionType={RunwayUserAction.UADownloadBuildArtifact}
                                                        onLinkAuthed={(authedDownloadLink) => {
                                                            window.open(authedDownloadLink, '_self');
                                                        }}
                                                        type="link"
                                                        linkUrl={
                                                            shouldUseWorkflowEndpoint ?
                                                                getWorkflowArtifactDownloadUrl(artifact)
                                                            :   artifact.downloadUrl
                                                        }
                                                        style={{ fontWeight: 600, padding: 0 }}
                                                    >
                                                        {artifact.fileName}
                                                    </AuthedUrlButton>
                                                )}
                                                {props.isPublic && (
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        onClick={() =>
                                                            window.open(
                                                                `${window.location.origin}${props.publicBuildDownloadUrl}?artifactFileName=${artifact.fileName}`,
                                                                '_self',
                                                            )
                                                        }
                                                    >
                                                        {artifact.fileName}
                                                    </Button>
                                                )}
                                                {sizeInMB > 0 && <Text type="secondary"> ({sizeInMB} MB)</Text>}
                                            </Space>
                                            {artifact.binaryBuildDetails && (
                                                <ArtifactInstallMenu
                                                    orgId={props.orgId}
                                                    appId={props.appId}
                                                    releaseId={props.releaseId}
                                                    artifact={artifact}
                                                />
                                            )}
                                        </div>
                                        {artifact.binaryBuildDetails && <BinaryBuildDetailsView artifact={artifact} />}
                                        {artifact.appStoreInfos && Object.keys(artifact.appStoreInfos).length !== 0 && (
                                            <UploadStatusSection>
                                                <Space
                                                    size={8}
                                                    split={
                                                        <Divider
                                                            type="vertical"
                                                            style={{
                                                                backgroundColor: antGrey.grey5,
                                                                height: 22,
                                                                marginTop: '-10px',
                                                            }}
                                                        />
                                                    }
                                                    wrap
                                                >
                                                    {Object.entries(artifact.appStoreInfos).map(
                                                        ([key, appStoreInfo]) => {
                                                            const service = props.services.get(key);
                                                            if (!service) {
                                                                return null;
                                                            }
                                                            return (
                                                                <ArtifactUploadInfo
                                                                    key={key}
                                                                    {...props}
                                                                    orgId={props.orgId}
                                                                    artifactId={artifact.artifactId}
                                                                    workflowRunId={artifact.workflowRunId}
                                                                    status={appStoreInfo.uploadStatus}
                                                                    appIdentifiers={props.appIdentifiers}
                                                                    service={service}
                                                                    errorMessage={appStoreInfo.errorMessage}
                                                                    trackName={appStoreInfo.trackName}
                                                                    canRetry={props.canRetry}
                                                                    rateLimitedUntil={appStoreInfo.rateLimitedUntil}
                                                                    createdAt={appStoreInfo.createdAt}
                                                                />
                                                            );
                                                        },
                                                    )}
                                                </Space>
                                            </UploadStatusSection>
                                        )}

                                        {artifact.dsymUploadInfo && props.stabilityMonitoring && (
                                            <UploadStatusSection>
                                                <Space>
                                                    <ArtifactUploadInfo
                                                        {...props}
                                                        orgId={props.orgId}
                                                        artifactId={artifact.artifactId}
                                                        workflowRunId={artifact.workflowRunId}
                                                        status={artifact.dsymUploadInfo.uploadStatus}
                                                        stabilityIntegrationProjectName={
                                                            artifact.dsymUploadInfo.projectName
                                                        }
                                                        service={props.stabilityMonitoring}
                                                        errorMessage={artifact.dsymUploadInfo.errorMessage}
                                                        canRetry={props.canRetry}
                                                        rateLimitedUntil={artifact.dsymUploadInfo.rateLimitedUntil}
                                                        createdAt={artifact.dsymUploadInfo.createdAt}
                                                    />
                                                </Space>
                                            </UploadStatusSection>
                                        )}
                                    </div>
                                </div>
                            </List.Item>
                        );
                    }}
                ></List>
            :   <Text style={{ color: antGrey.grey6 }}>{emptyStateText}</Text>}
        </div>
    );
};

export interface ArtifactUploadInfoTagProps {
    status: AppStoreUploadArtifactStatus | DsymUploadArtifactStatus;
    service: AppDistributionService | StabilityMonitoringService;
    appIdentifiers: Map<IntegrationCapability, string>; // used for app store upload
    stabilityIntegrationProjectName?: string; // used for dsym upload
    trackName?: string;
    errorMessage?: string;
}

export const ArtifactUploadInfoTag: React.FC<ArtifactUploadInfoTagProps> = (props) => {
    const isBetaTesting =
        isAppDistributionService(props.service.id) &&
        props.service.capabilities.includes(IntegrationCapability.BetaTesting);
    const isAppStore =
        isAppDistributionService(props.service.id) &&
        props.service.capabilities.includes(IntegrationCapability.AppStore);
    const isStabilityMonitoring = isStabilityMonitoringService(props.service.id);

    const identifierLabel = () => {
        if (isStabilityMonitoring) {
            return props.stabilityIntegrationProjectName ?? '';
        } else if (isBetaTesting && props.appIdentifiers) {
            return props.appIdentifiers?.[IntegrationCapability.BetaTesting] ?? '';
        } else if (isAppStore && props.appIdentifiers) {
            return props.appIdentifiers?.[IntegrationCapability.AppStore] ?? '';
        }
        return '';
    };

    const [icon, alt] = iconAndAltForIntegrationType(props.service.id);

    const tag = (
        <Tag
            style={{
                ...tagStyle,
                fontSize: 10,
                borderColor: statusTagColor(props.status),
                color: statusTagColor(props.status),
            }}
        >
            <Space size={8}>
                {statusTagText(props.status)}

                {props.errorMessage && props.status === AppStoreUploadArtifactStatus.AppStoreUploadError && (
                    <InfoCircleOutlined style={{ color: statusTagColor(props.status) }} />
                )}

                {props.status === AppStoreUploadArtifactStatus.AppStoreUploadBlocked && (
                    <Tooltip
                        title={`This artifact’s upload is blocked because a previous build artifact upload failed. To unblock this upload, resolve previous upload failures, or change the “Upload behavior for multiple builds” setting for this automation (currently set to ${displayTextForAppStoreUploadRule}).`}
                    >
                        <InfoCircleOutlined style={{ color: statusTagColor(props.status) }} />
                    </Tooltip>
                )}
            </Space>
        </Tag>
    );

    const isAppStoreUploadError = props.status === AppStoreUploadArtifactStatus.AppStoreUploadError;
    const appStoreUploadErrorMessage = props.errorMessage && isAppStoreUploadError ? props.errorMessage : null;
    const showErrorModal = appStoreUploadErrorMessage && appStoreUploadErrorMessage.length > MAX_ERROR_MESSAGE_LENGTH;

    const showAppStoreUploadErrorModal = () => {
        Modal.error({
            title: 'App Store Upload Error',
            content: <Text>{appStoreUploadErrorMessage}</Text>,
            okText: 'Close',
        });
    };

    return (
        <Space>
            {icon && <img src={icon} alt={alt} style={{ height: 16, width: 16 }} />}
            <Text style={{ color: antGrey.grey8 }}>
                {props.service.name}
                {identifierLabel() && ` (${identifierLabel()})`}
            </Text>
            {props.trackName && (
                <>
                    <Divider type="vertical" />
                    <Text style={{ color: antGrey.grey8 }}>{props.trackName}</Text>
                </>
            )}
            {showErrorModal && (
                <Button type="link" onClick={showAppStoreUploadErrorModal}>
                    {tag}
                </Button>
            )}
            {!showErrorModal && <Tooltip title={appStoreUploadErrorMessage}>{tag}</Tooltip>}
        </Space>
    );
};

export interface ArtifactUploadStatusProps {
    artifact: BuildArtifact | BuildDistroArtifactInfo;
    appIdentifiers: Map<IntegrationCapability, string>;
    distributionServices: Map<IntegrationInstallationId, AppDistributionService>;
}

export const ArtifactUploadStatus: React.FC<ArtifactUploadStatusProps> = ({
    artifact,
    appIdentifiers,
    distributionServices,
}) => {
    return (
        <Space
            size={8}
            split={
                <Divider
                    type="vertical"
                    style={{
                        backgroundColor: antGrey.grey5,
                        height: 22,
                        marginTop: '-10px',
                    }}
                />
            }
            wrap
        >
            {Object.entries(artifact.appStoreInfos).map(([installationId, appStoreInfo]) => (
                <ArtifactUploadInfoTag
                    key={installationId}
                    status={appStoreInfo.uploadStatus}
                    appIdentifiers={appIdentifiers}
                    service={distributionServices.get(installationId)}
                    errorMessage={appStoreInfo.errorMessage}
                    trackName={appStoreInfo.trackName}
                />
            ))}
        </Space>
    );
};

interface ArtifactUploadInfoProps extends ReleaseComponentProps {
    artifactId: string;
    workflowRunId: string;
    status: AppStoreUploadArtifactStatus | DsymUploadArtifactStatus;
    canRetry: boolean;
    appIdentifiers?: Map<IntegrationCapability, string>; // used for app store upload
    stabilityIntegrationProjectName?: string; // used for dsym upload
    service: AppDistributionService | StabilityMonitoringService;
    errorMessage?: string;
    trackName?: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    rateLimitedUntil?: string;
    createdAt?: string;
}

export const ArtifactUploadInfo: React.FC<ArtifactUploadInfoProps> = (props: ArtifactUploadInfoProps) => {
    const isBetaTesting =
        isAppDistributionService(props.service.id) &&
        props.service.capabilities.includes(IntegrationCapability.BetaTesting);
    const isAppStore =
        isAppDistributionService(props.service.id) &&
        props.service.capabilities.includes(IntegrationCapability.AppStore);
    const installationId = props.service.installationId;
    const isStabilityMonitoring = isStabilityMonitoringService(props.service.id);

    const stepRunId = props.stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const retryIdentifier = () => {
        if (isStabilityMonitoring) {
            return props.artifactId + '-' + 'StabilityMonitoring';
        } else if (isBetaTesting) {
            return props.artifactId + '-' + 'BetaTesting';
        } else if (isAppStore) {
            return props.artifactId + '-' + 'AppStore';
        }
        return '';
    };

    const [
        retryArtifactUpload,
        retryArtifactUploadIsLoadingStepRun,
        retryArtifactUploadResponseTypeStepRun,
        retryArtifactUploadErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.RetryArtifactUpload, props.stepRunComponentProps, retryIdentifier());
    const [
        retryDsymUpload,
        retryDsymUploadIsLoadingStepRun,
        retryDsymUploadResponseTypeStepRun,
        retryDsymUploadErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.RetryDsymUpload, props.stepRunComponentProps, retryIdentifier());

    let uploadTypeString: string;
    let isLoading: boolean;
    let retryUploadError: string;
    let retryUploadSuccess: boolean;
    let releaseUpdatable: ReleaseUpdatable;
    if (isStabilityMonitoring) {
        uploadTypeString = 'dSYM';
        releaseUpdatable = ReleaseUpdatable.RetryDsymUpload;

        const isLoadingReleaseStep = useIsLoading(releaseUpdatable, retryIdentifier());
        const retryUploadErrorReleaseStep = useError(releaseUpdatable, retryIdentifier());
        const retryUploadSuccessReleaseStep = useUpdated(releaseUpdatable, retryIdentifier());

        isLoading = stepRun ? retryDsymUploadIsLoadingStepRun : isLoadingReleaseStep;
        retryUploadError = stepRun ? retryDsymUploadErrorStepRun : retryUploadErrorReleaseStep;
        retryUploadSuccess =
            stepRun ? retryDsymUploadResponseTypeStepRun === RunwayResponseType.Success : retryUploadSuccessReleaseStep;
    } else if (isBetaTesting || isAppStore) {
        uploadTypeString = 'artifact';
        releaseUpdatable = ReleaseUpdatable.RetryAppStoreArtifactUpload;

        const isLoadingReleaseStep = useIsLoading(releaseUpdatable, retryIdentifier());
        const retryUploadErrorReleaseStep = useError(releaseUpdatable, retryIdentifier());
        const retryUploadSuccessReleaseStep = useUpdated(releaseUpdatable, retryIdentifier());

        isLoading = stepRun ? retryArtifactUploadIsLoadingStepRun : isLoadingReleaseStep;
        retryUploadError = stepRun ? retryArtifactUploadErrorStepRun : retryUploadErrorReleaseStep;
        retryUploadSuccess =
            stepRun ?
                retryArtifactUploadResponseTypeStepRun === RunwayResponseType.Success
            :   retryUploadSuccessReleaseStep;
    }

    React.useEffect(() => {
        if (retryUploadError) {
            globalMessage.error(retryUploadError);
        }
    }, [retryUploadError]);

    React.useEffect(() => {
        if (retryUploadSuccess) {
            globalMessage.success(
                `Your ${uploadTypeString} upload retry has been initiated! It may take a few seconds for the updated status to appear in Runway.`,
            );
        }
    }, [retryUploadSuccess]);

    const dispatch = useDispatch();

    const useHasPermissionToRetry = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UARetryArtifactUpload, null, null, props.appId),
    );

    const onClickRetryArtifactUpload = () => {
        if (isStabilityMonitoringService(props.service.id)) {
            if (stepRun) {
                return retryDsymUpload({
                    workflowRunId: props.workflowRunId,
                    artifactId: props.artifactId,
                });
            }

            const request: RetryDsymUploadRequest = {
                workflowRunId: props.workflowRunId,
                artifactId: props.artifactId,
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.RetryDsymUpload, request, retryIdentifier()));
        } else if (isAppDistributionService(props.service.id) && (isBetaTesting || isAppStore)) {
            if (stepRun) {
                return retryArtifactUpload({
                    workflowRunId: props.workflowRunId,
                    artifactId: props.artifactId,
                    destinationInstallationId: installationId,
                    trackName: props.trackName,
                });
            }

            const request: RetryAppStoreArtifactUploadRequest = {
                workflowRunId: props.workflowRunId,
                artifactId: props.artifactId,
                destinationInstallationId: installationId,
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.RetryAppStoreArtifactUpload, request, retryIdentifier()));
        }
    };

    const allowRetryWhileUploadPending = React.useMemo(() => {
        const isPendingStatus =
            props.status === AppStoreUploadArtifactStatus.AppStoreUploadPending ||
            props.status === DsymUploadArtifactStatus.DsymUploadPending;

        if (!isPendingStatus) return null;
        if (!props.createdAt) return false;

        return dayjs().diff(dayjs(props.createdAt), 'minute') >= 15;
    }, [props.status, props.createdAt]);

    const isARetryInProgress =
        props.status === AppStoreUploadArtifactStatus.AppStoreUploadRetryRequested ||
        allowRetryWhileUploadPending === false ||
        props.status === AppStoreUploadArtifactStatus.AppStoreUploadInProgress ||
        props.status === AppStoreUploadArtifactStatus.AppStoreUploadRetrying ||
        props.status === DsymUploadArtifactStatus.DsymUploadInProgress ||
        props.status === DsymUploadArtifactStatus.DsymUploadRetrying;

    const userHasHitRateLimit = props.rateLimitedUntil && new Date(props.rateLimitedUntil) > new Date();

    const isUploadSuccessful =
        props.status === AppStoreUploadArtifactStatus.AppStoreUploadSuccess ||
        props.status === DsymUploadArtifactStatus.DsymUploadSuccess;

    const isRetryButtonDisabled =
        !props.canRetry || !useHasPermissionToRetry || userHasHitRateLimit || isARetryInProgress;

    const getDisabledTooltipMessage = () => {
        if (!props.canRetry) {
            return 'Retrying is not supported for this upload';
        }
        if (!useHasPermissionToRetry) {
            return "You don't have permission to retry artifact uploads";
        }
        if (userHasHitRateLimit) {
            return `Rate limited until ${dayjs(props.rateLimitedUntil).tz(effectiveTimezone).format(DatetimeFormat.ShortMonthDateYear)}`;
        }
        if (isARetryInProgress) {
            return 'A retry is already in progress';
        }
        return '';
    };

    const retryButton = (
        <Button
            icon={<ReloadOutlined />}
            type="link"
            style={{ color: runwayColors.primary, padding: 0, paddingTop: -10, height: '22px', margin: 0 }}
            onClick={onClickRetryArtifactUpload}
            loading={isLoading}
            disabled={isRetryButtonDisabled}
        >
            Retry
        </Button>
    );

    return (
        <Space style={{ paddingBottom: 12 }}>
            <ArtifactUploadInfoTag
                {...props}
                appIdentifiers={props.appIdentifiers}
                stabilityIntegrationProjectName={props.stabilityIntegrationProjectName}
            />

            {isUploadSuccessful ?
                null
            : isRetryButtonDisabled ?
                <Tooltip title={getDisabledTooltipMessage()}>{retryButton}</Tooltip>
            :   retryButton}
        </Space>
    );
};

export default ArtifactsModule;

enum InstallOptions {
    DisplayQRCode,
    SendToComms,
}

export interface ArtifactInstallMenuProps extends AppComponentProps {
    artifact: BuildArtifact;
    releaseId?: string;
}

export const ArtifactInstallMenu: React.FC<ArtifactInstallMenuProps> = (props) => {
    const { orgId, appId, releaseId } = props;
    const dispatch = useDispatch();

    const [sendInstallLinkToCommsIsLoading, setSendInstallLinkToCommsIsLoading] = React.useState(false);
    const [isLoadingQRCode, setIsLoadingQRCode] = React.useState(false);
    const [qrCodeLink, setQRCodeLink] = React.useState(null);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const artifactType = props.artifact.binaryBuildDetails?.artifactType;

    const provisioningProfileName = props.artifact.binaryBuildDetails?.provisioningProfile?.name;

    const version = props.artifact.version;
    const artifactId = props.artifact.artifactId;
    const workflowRunId = props.artifact.workflowRunId;

    const isDisabled = getShouldDisableInstallationForBinaryType(artifactType);
    const tooltipText = helperTextForArtifactType(artifactType, provisioningProfileName);
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );
    const [icon, alt] = iconAndAltForIntegrationType(commsIntegration?.id);
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    const menuItems: MenuItemType[] = [
        {
            label: (
                <Space align="center">
                    <QrcodeOutlined />
                    <Text>Install from QR Code</Text>
                </Space>
            ),
            key: InstallOptions.DisplayQRCode,
        },
        {
            label: (
                <Space align="center">
                    {icon && <img src={icon} alt={alt} style={{ width: 16 }} />}
                    <Text>Share install link to {commsProviders}</Text>
                    <Tooltip
                        title={`A link to the installation page for this build will be sent to your app's primary ${commsProviders} channel`}
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                    </Tooltip>
                </Space>
            ),
            key: InstallOptions.SendToComms,
        },
    ];

    const onClickMenuItem = (item) => {
        switch (parseInt(item.key)) {
            case InstallOptions.SendToComms: {
                dispatch(
                    trackReleaseRouteEvent({
                        orgId,
                        appId,
                        releaseId,
                        eventName: AnalyticEvents.Event.ClickShareInstallLinkToComms,
                    }),
                );
                setSendInstallLinkToCommsIsLoading(true);
                API.sendBinaryInstallLinkToComms(
                    {
                        orgId,
                        appId,
                        version,
                        artifactId,
                        workflowRunId,
                    },
                    authCredentials,
                )
                    .then(() => {
                        globalMessage.success('Install link sent!');
                    })
                    .catch((error) => {
                        globalMessage.error(error.message);
                    })
                    .finally(() => setSendInstallLinkToCommsIsLoading(false));
                break;
            }
            case InstallOptions.DisplayQRCode: {
                setIsLoadingQRCode(true);
                API.generateBinaryInstallLink(
                    {
                        orgId,
                        appId,
                        workflowRunId,
                        artifactId,
                        version,
                    },
                    authCredentials,
                )
                    .then((link) => {
                        setQRCodeLink(link);
                    })
                    .catch((error) => {
                        globalMessage.error(error.message);
                    })
                    .finally(() => {
                        setIsLoadingQRCode(false);
                    });
                break;
            }
        }
    };

    const menuProps: MenuProps = {
        items: menuItems,
        onClick: onClickMenuItem,
    };

    if (props.artifact?.binaryBuildDetails === null) {
        return null;
    }

    const isLoading = sendInstallLinkToCommsIsLoading || isLoadingQRCode;

    return (
        <>
            <Modal open={!!qrCodeLink} onCancel={() => setQRCodeLink(null)} destroyOnClose footer={null}>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <QRCode value={qrCodeLink} size={420} />
                </div>
            </Modal>
            <Space align="center">
                <Dropdown menu={menuProps} trigger={['click']} disabled={isDisabled}>
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UADownloadBuildArtifact}
                        loading={isLoading}
                        disabled={isDisabled}
                        icon={<MobileOutlined style={{ color: runwayColors.primary }} />}
                    >
                        <Space style={{ marginLeft: 8 }}>
                            Install
                            <DownOutlined />
                        </Space>
                    </PermissionedActionButton>
                </Dropdown>
                <Tooltip title={tooltipText}>
                    <InfoCircleOutlined
                        style={{
                            color: runwayColors.info,
                        }}
                    />
                </Tooltip>
            </Space>
        </>
    );
};
